import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOSchema from '@/components/feature/SEOSchema';
import { mangaArticles, categories } from '@/mocks/mangaData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const categoryInfo = useMemo(() => categories.find(c => c.slug === slug), [slug]);

  const filteredArticles = useMemo(() => {
    if (!slug || slug === 'all') return mangaArticles;
    return mangaArticles.filter(a => a.categorySlug === slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  if (!categoryInfo && slug !== 'all') {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="font-heading text-2xl text-foreground-900">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = categoryInfo
    ? `${categoryInfo.name}bl漫画 - ${categoryInfo.description}`
    : '全部bl漫画 - bl漫画精选推荐';

  const pageDesc = categoryInfo
    ? `精选${categoryInfo.name}题材bl漫画推荐，${categoryInfo.description}。免费在线畅读高质量${categoryInfo.name}bl漫画作品。`
    : '浏览全部bl漫画推荐作品，涵盖甜宠、虐心、古风、现代等多种题材，每日更新最新bl漫画内容。';

  return (
    <>
      <SEOSchema
        type="WebPage"
        pageTitle={pageTitle}
        pageDescription={pageDesc}
        pageUrl={`/category/${slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryInfo ? categoryInfo.name : '全部' },
                ]}
              />
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-6" aria-label="分类内容">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-2">
                  {categoryInfo ? `${categoryInfo.name}bl漫画` : '全部bl漫画'}
                </h1>
                <p className="text-foreground-500 text-sm">{categoryInfo?.description || pageDesc}</p>
              </div>

              {/* Category tabs */}
              <div className="flex flex-wrap gap-2 mb-8">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as const })}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 border border-background-200/70'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                    <i className="ri-book-open-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无作品</p>
                  <Link
                    to="/category/all"
                    className="text-primary-500 hover:text-primary-600 text-sm mt-2 inline-block"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as const })}
                  >
                    浏览全部作品
                  </Link>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}